import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiFout, api } from '@/lib/api';
import type {
  AiTestIcpc,
  AiTestRun,
  AiTestRunDetail,
  AiTestRunSamenvatting,
  AiTestTranscriptie,
  AiTestUitkomst,
} from '@/lib/api-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  AlertTriangle,
  Check,
  Loader2,
  Pencil,
  Play,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * De AI-testset: transcripties vastleggen, de hele set laten draaien, en een run
 * naast een eerdere leggen.
 *
 * De losse test in AiSettingsCard doet één transcriptie en toont het antwoord. Dat
 * antwoord verdwijnt bij de volgende klik, en daarmee de vraag die bij het
 * wijzigen van een model of een prompt telt: is het beter geworden of slechter?
 * Hier blijft elke uitkomst met een datum staan, zodat die vraag te beantwoorden
 * is met twee kolommen naast elkaar in plaats van met een herinnering.
 *
 * De run loopt vanuit dit scherm, één transcriptie per verzoek. Zie
 * apps/api/src/routes/admin-ai-tests.ts voor waarom dat niet één verzoek is: bij
 * twintig transcripties van een minuut verbreekt de proxy de verbinding voordat
 * het antwoord er is, en dan is het werk gedaan maar het resultaat weg. Nu staat
 * elke uitkomst al in de database voordat de volgende begint -- een afgebroken run
 * verliest niets.
 */

const SOEP_VELDEN = [
  { sleutel: 'subjective', letter: 'S', naam: 'Subjectief' },
  { sleutel: 'objective', letter: 'O', naam: 'Objectief' },
  { sleutel: 'evaluation', letter: 'E', naam: 'Evaluatie' },
  { sleutel: 'plan', letter: 'P', naam: 'Plan' },
] as const;

type SoepSleutel = (typeof SOEP_VELDEN)[number]['sleutel'];

const VOORBEELD_TRANSCRIPT = `[Arts]: Goedemorgen, wat kan ik voor u doen?
[Patiënt]: Ik heb al een paar dagen last van hoofdpijn, vooral achter mijn ogen. En ik voel me ook een beetje duizelig.
[Arts]: Hoe lang heeft u hier precies last van?
[Patiënt]: Sinds maandag, dus nu vier dagen.
[Arts]: Heeft u ook last van misselijkheid of braken?
[Patiënt]: Nee, alleen de hoofdpijn en duizeligheid.
[Arts]: Gebruikt u medicijnen?
[Patiënt]: Alleen af en toe een paracetamol, maar dat helpt niet echt.
[Arts]: Ik ga uw bloeddruk even meten. Die is 145 over 92, dat is wat aan de hoge kant. Ik wil graag bloedonderzoek laten doen om een paar dingen uit te sluiten.
[Patiënt]: Oké, dat is goed.
[Arts]: Ik denk dat het spanningshoofdpijn kan zijn, mogelijk in combinatie met licht verhoogde bloeddruk. Ik schrijf ibuprofen voor en we plannen een controleafspraak over twee weken.`;

// --- Vergelijken ----------------------------------------------------------

type DiffSoort = 'gelijk' | 'weg' | 'nieuw';

interface DiffStuk {
  soort: DiffSoort;
  tekst: string;
}

/** Losse woorden, met de witruimte eraan vast zodat samenvoegen de tekst teruggeeft. */
function splitsInWoorden(tekst: string): string[] {
  return tekst.match(/\S+\s*/g) ?? [];
}

/**
 * Verschil per woord tussen twee versies van hetzelfde SOEP-veld.
 *
 * Twee teksten naast elkaar zetten laat wél zien dat er iets is veranderd, maar
 * niet wát -- en bij een verslag van tien regels is dat precies wat je wil weten.
 * Vandaar een woorddiff via de langste gemeenschappelijke deelrij.
 *
 * De tabel is O(n*m). Een SOEP-veld is enkele honderden woorden, dus dat is hier
 * ruim binnen de proporties; boven een plafond valt hij terug op "de hele tekst is
 * vervangen", want een diff die de browser laat haperen helpt niemand.
 */
function woordDiff(oud: string, nieuw: string): DiffStuk[] {
  const a = splitsInWoorden(oud);
  const b = splitsInWoorden(nieuw);

  if (a.length === 0 && b.length === 0) return [];
  if (a.length === 0) return [{ soort: 'nieuw', tekst: nieuw }];
  if (b.length === 0) return [{ soort: 'weg', tekst: oud }];

  if (a.length * b.length > 250_000) {
    return [
      { soort: 'weg', tekst: oud },
      { soort: 'nieuw', tekst: nieuw },
    ];
  }

  // lcs[i][j] = lengte van de langste gemeenschappelijke deelrij van a[i..] en b[j..].
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0),
  );

  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      lcs[i][j] =
        a[i].trim() === b[j].trim()
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const stukken: DiffStuk[] = [];
  const voegToe = (soort: DiffSoort, tekst: string) => {
    const laatste = stukken[stukken.length - 1];
    if (laatste && laatste.soort === soort) laatste.tekst += tekst;
    else stukken.push({ soort, tekst });
  };

  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i].trim() === b[j].trim()) {
      voegToe('gelijk', b[j]);
      i += 1;
      j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      voegToe('weg', a[i]);
      i += 1;
    } else {
      voegToe('nieuw', b[j]);
      j += 1;
    }
  }
  while (i < a.length) {
    voegToe('weg', a[i]);
    i += 1;
  }
  while (j < b.length) {
    voegToe('nieuw', b[j]);
    j += 1;
  }

  return stukken;
}

/** Witruimte-verschillen zijn geen wijziging. */
function gelijkNaNormalisatie(oud: string, nieuw: string): boolean {
  return oud.replace(/\s+/g, ' ').trim() === nieuw.replace(/\s+/g, ' ').trim();
}

function DiffTekst({ oud, nieuw }: { oud: string; nieuw: string }) {
  const stukken = useMemo(() => woordDiff(oud, nieuw), [oud, nieuw]);

  if (stukken.length === 0) {
    return <span className="text-muted-foreground italic">leeg</span>;
  }

  return (
    <span className="whitespace-pre-wrap">
      {stukken.map((stuk, index) => {
        if (stuk.soort === 'gelijk') return <span key={index}>{stuk.tekst}</span>;
        return (
          <span
            key={index}
            className={
              stuk.soort === 'weg'
                ? 'bg-destructive/15 text-destructive line-through decoration-destructive/60'
                : 'bg-emerald-500/20 text-emerald-800 dark:text-emerald-300'
            }
          >
            {stuk.tekst}
          </span>
        );
      })}
    </span>
  );
}

/**
 * De ICPC-suggestie bij een uitkomst, met het verschil ten opzichte van de vorige
 * run.
 *
 * Waarom codes en niet de omschrijvingen worden vergeleken: de omschrijving komt
 * uit de codelijst en verandert niet door een modelwissel. Wat wél verandert is
 * welke codes het model kiest, en of het codes verzint.
 */
function IcpcRegel({ nu, toen }: { nu: AiTestIcpc; toen: AiTestIcpc | null }) {
  // Niet gevraagd: geen ICPC-stap gedaan (uitkomst van vóór deze stap, of de
  // SOEP-aanroep faalde en dan is er geen evaluatietekst). Dat is iets anders dan
  // "geen codes gevonden", dus het hoort ook anders te lezen.
  if (nu.codes === null && !nu.error) {
    return (
      <p className="text-xs text-muted-foreground border-t pt-2 mt-1">
        ICPC: niet gevraagd
      </p>
    );
  }

  if (nu.error) {
    return (
      <div className="border-t pt-2 mt-1 space-y-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          ICPC
        </p>
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-2 text-sm text-destructive flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{nu.error}</span>
        </div>
      </div>
    );
  }

  const nuCodes = (nu.codes ?? []).map((c) => c.code);
  const toenCodes = toen?.codes === null || toen === null ? null : toen.codes.map((c) => c.code);

  const erbij = toenCodes ? nuCodes.filter((c) => !toenCodes.includes(c)) : [];
  const eraf = toenCodes ? toenCodes.filter((c) => !nuCodes.includes(c)) : [];
  const ongewijzigd = toenCodes !== null && erbij.length === 0 && eraf.length === 0;

  return (
    <div className="border-t pt-2 mt-1 space-y-1">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          ICPC
        </p>
        {ongewijzigd && (
          <Badge variant="secondary" className="text-[10px]">
            ongewijzigd
          </Badge>
        )}
        {(erbij.length > 0 || eraf.length > 0) && (
          <Badge variant="default" className="text-[10px]">
            {erbij.length > 0 && `+${erbij.join(', ')}`}
            {erbij.length > 0 && eraf.length > 0 && ' '}
            {eraf.length > 0 && `-${eraf.join(', ')}`}
          </Badge>
        )}
      </div>

      {nuCodes.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">geen codes gevonden</p>
      ) : (
        <div className="space-y-0.5 text-sm">
          {(nu.codes ?? []).map((c) => (
            <p key={c.code}>
              <span
                className={`font-mono font-semibold ${erbij.includes(c.code) ? 'text-emerald-700 dark:text-emerald-400' : 'text-primary'}`}
              >
                {c.code}
              </span>{' '}
              <span className="text-muted-foreground">—</span>{' '}
              {c.description ?? (
                <span className="text-muted-foreground italic">
                  staat niet meer in de codelijst
                </span>
              )}
            </p>
          ))}
        </div>
      )}

      {eraf.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Vorige run had ook:{' '}
          <span className="font-mono line-through decoration-destructive/60">
            {eraf.join(', ')}
          </span>
        </p>
      )}

      {/**
       * Verzonnen codes. In een consult verdwijnen die stil, en dat hoort daar --
       * maar hier is het het interessantste getal: een model dat codes verzint
       * gedraagt zich anders dan een model dat er geen weet.
       */}
      {nu.rejected.length > 0 && (
        <p className="text-xs text-destructive">
          Afgekeurd (bestaan niet):{' '}
          <span className="font-mono">{nu.rejected.join(', ')}</span>
        </p>
      )}
    </div>
  );
}

// --- Opmaak ---------------------------------------------------------------

function datumTijd(waarde: string): string {
  return new Date(waarde).toLocaleString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function runLabel(run: AiTestRunSamenvatting): string {
  const kern = `${datumTijd(run.startedAt)} — ${run.model ?? 'onbekend model'}`;
  return run.label ? `${kern} — ${run.label}` : kern;
}

// --- Het paneel -----------------------------------------------------------

interface Voortgang {
  totaal: number;
  klaar: number;
  bezigMet: string;
}

/** Het formulier voor een nieuwe of te wijzigen transcriptie. */
interface Concept {
  id: string | null;
  name: string;
  transcript: string;
  multipleProblems: boolean;
  notes: string;
}

const LEEG_CONCEPT: Concept = {
  id: null,
  name: '',
  transcript: '',
  multipleProblems: false,
  notes: '',
};

export function AiTestSuitePanel() {
  const [transcripties, setTranscripties] = useState<AiTestTranscriptie[]>([]);
  const [runs, setRuns] = useState<AiTestRunSamenvatting[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [concept, setConcept] = useState<Concept | null>(null);
  const [isBewaren, setIsBewaren] = useState(false);
  const [teVerwijderen, setTeVerwijderen] = useState<AiTestTranscriptie | null>(null);

  const [runLabelInvoer, setRunLabelInvoer] = useState('');
  const [voortgang, setVoortgang] = useState<Voortgang | null>(null);

  const [gekozenRun, setGekozenRun] = useState<string | null>(null);
  const [vergelijkRun, setVergelijkRun] = useState<string | null>(null);
  const [detail, setDetail] = useState<AiTestRunDetail | null>(null);
  const [vergelijkDetail, setVergelijkDetail] = useState<AiTestRunDetail | null>(null);
  const [isDetailLaden, setIsDetailLaden] = useState(false);

  const laadTestset = useCallback(async () => {
    const [{ transcripts }, { runs: gevonden }] = await Promise.all([
      api.get<{ transcripts: AiTestTranscriptie[] }>('/admin/ai/test-transcripts'),
      api.get<{ runs: AiTestRunSamenvatting[] }>('/admin/ai/test-runs?limit=50'),
    ]);
    setTranscripties(transcripts);
    setRuns(gevonden);
    return gevonden;
  }, []);

  useEffect(() => {
    void (async () => {
      setIsLoading(true);
      try {
        const gevonden = await laadTestset();
        // De nieuwste run staat vooraan; die is bij openen het interessantst, met
        // de run ervoor als vergelijking.
        if (gevonden[0]) setGekozenRun(gevonden[0].id);
        if (gevonden[1]) setVergelijkRun(gevonden[1].id);
      } catch (fout) {
        toast.error(fout instanceof ApiFout ? fout.message : 'Kon de testset niet laden');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [laadTestset]);

  // De uitkomsten van de gekozen run, en die van de run waarmee vergeleken wordt.
  useEffect(() => {
    if (!gekozenRun) {
      setDetail(null);
      return;
    }

    let afgebroken = false;
    void (async () => {
      setIsDetailLaden(true);
      try {
        const antwoord = await api.get<AiTestRunDetail>(`/admin/ai/test-runs/${gekozenRun}`);
        if (!afgebroken) setDetail(antwoord);
      } catch (fout) {
        if (!afgebroken) {
          toast.error(fout instanceof ApiFout ? fout.message : 'Kon de run niet laden');
        }
      } finally {
        if (!afgebroken) setIsDetailLaden(false);
      }
    })();

    return () => {
      afgebroken = true;
    };
  }, [gekozenRun]);

  useEffect(() => {
    if (!vergelijkRun || vergelijkRun === gekozenRun) {
      setVergelijkDetail(null);
      return;
    }

    let afgebroken = false;
    void (async () => {
      try {
        const antwoord = await api.get<AiTestRunDetail>(`/admin/ai/test-runs/${vergelijkRun}`);
        if (!afgebroken) setVergelijkDetail(antwoord);
      } catch (fout) {
        if (!afgebroken) {
          toast.error(
            fout instanceof ApiFout ? fout.message : 'Kon de vergelijkingsrun niet laden',
          );
        }
      }
    })();

    return () => {
      afgebroken = true;
    };
  }, [vergelijkRun, gekozenRun]);

  const actieveTranscripties = transcripties.filter((t) => t.isActive);

  // --- De testset bewerken ------------------------------------------------

  const bewaarConcept = async () => {
    if (!concept) return;

    const naam = concept.name.trim();
    const tekst = concept.transcript.trim();
    if (!naam || !tekst) {
      toast.error('Een naam en een transcriptie zijn beide nodig');
      return;
    }

    setIsBewaren(true);
    try {
      if (concept.id) {
        await api.patch(`/admin/ai/test-transcripts/${concept.id}`, {
          name: naam,
          transcript: tekst,
          multipleProblems: concept.multipleProblems,
          notes: concept.notes.trim() || null,
        });
        toast.success('Testtranscriptie bijgewerkt');
      } else {
        await api.post('/admin/ai/test-transcripts', {
          name: naam,
          transcript: tekst,
          multipleProblems: concept.multipleProblems,
          notes: concept.notes.trim() || null,
        });
        toast.success('Testtranscriptie toegevoegd');
      }
      setConcept(null);
      await laadTestset();
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Opslaan mislukt');
    } finally {
      setIsBewaren(false);
    }
  };

  const zetActief = async (transcriptie: AiTestTranscriptie, actief: boolean) => {
    // Optimistisch: de schakelaar hoort niet te wachten op een rondje naar de server.
    setTranscripties((huidig) =>
      huidig.map((t) => (t.id === transcriptie.id ? { ...t, isActive: actief } : t)),
    );
    try {
      await api.patch(`/admin/ai/test-transcripts/${transcriptie.id}`, { isActive: actief });
    } catch (fout) {
      setTranscripties((huidig) =>
        huidig.map((t) => (t.id === transcriptie.id ? { ...t, isActive: !actief } : t)),
      );
      toast.error(fout instanceof ApiFout ? fout.message : 'Wijzigen mislukt');
    }
  };

  const verwijderTranscriptie = async () => {
    if (!teVerwijderen) return;
    try {
      await api.delete(`/admin/ai/test-transcripts/${teVerwijderen.id}`);
      toast.success('Testtranscriptie verwijderd');
      setTeVerwijderen(null);
      await laadTestset();
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Verwijderen mislukt');
      setTeVerwijderen(null);
    }
  };

  // --- Een run draaien ----------------------------------------------------

  const draaiTestset = async () => {
    setVoortgang({ totaal: actieveTranscripties.length, klaar: 0, bezigMet: '' });

    try {
      const start = await api.post<{
        run: AiTestRun;
        transcripts: { id: string; name: string; multipleProblems: boolean }[];
      }>('/admin/ai/test-runs', { label: runLabelInvoer.trim() || undefined });

      // De vorige run wordt de vergelijking; de nieuwe komt ernaast te staan.
      const vorige = runs[0]?.id ?? null;
      setGekozenRun(start.run.id);
      setVergelijkRun(vorige);
      setDetail({ run: start.run, results: [] });
      setVoortgang({ totaal: start.transcripts.length, klaar: 0, bezigMet: '' });

      let mislukt = 0;
      for (const [index, transcriptie] of start.transcripts.entries()) {
        setVoortgang({
          totaal: start.transcripts.length,
          klaar: index,
          bezigMet: transcriptie.name,
        });

        try {
          const { result } = await api.post<{ result: AiTestUitkomst }>(
            `/admin/ai/test-runs/${start.run.id}/items`,
            { transcriptId: transcriptie.id },
          );
          if (result.error) mislukt += 1;

          /**
           * De uitkomst er meteen bij zetten in plaats van pas na de hele run.
           * Bij twintig transcripties duurt dat minuten, en dan staat er al die
           * tijd een lege lijst onder de voortgangsbalk terwijl de vergelijking
           * per transcriptie al te lezen is.
           */
          setDetail((huidig) => {
            const basis =
              huidig && huidig.run.id === start.run.id
                ? huidig
                : { run: start.run, results: [] };
            const zonderOude = basis.results.filter(
              (u) => u.transcriptId !== result.transcriptId,
            );
            return {
              run: basis.run,
              results: [...zonderOude, result].sort((a, b) =>
                (a.transcriptName ?? '').localeCompare(b.transcriptName ?? '', 'nl'),
              ),
            };
          });
        } catch (fout) {
          /**
           * Een fout die hier terugkomt geldt voor de hele run -- de route bewaart
           * een mislukte AI-aanroep als uitkomst en geeft dan 200; alleen een
           * configuratiefout komt als fout terug. Doorgaan zou dezelfde melding
           * nog negentien keer opleveren.
           */
          await api.post(`/admin/ai/test-runs/${start.run.id}/finish`).catch(() => undefined);
          throw fout;
        }
      }

      setVoortgang({
        totaal: start.transcripts.length,
        klaar: start.transcripts.length,
        bezigMet: '',
      });
      await api.post(`/admin/ai/test-runs/${start.run.id}/finish`);

      if (mislukt > 0) {
        toast.warning(
          `Run klaar: ${start.transcripts.length - mislukt} van ${start.transcripts.length} geslaagd`,
        );
      } else {
        toast.success(`Run klaar: ${start.transcripts.length} transcriptie(s) gedraaid`);
      }

      setRunLabelInvoer('');
      await laadTestset();
      // Nog één keer van de server, zodat finishedAt en de tokentotalen kloppen
      // met wat er is opgeslagen -- het scherm heeft de uitkomsten onderweg zelf
      // bijgehouden, en dat is een tweede waarheid tot dit rondje.
      const opnieuw = await api.get<AiTestRunDetail>(`/admin/ai/test-runs/${start.run.id}`);
      setDetail(opnieuw);
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'De testrun is mislukt');
      await laadTestset().catch(() => undefined);
    } finally {
      setVoortgang(null);
    }
  };

  const verwijderRun = async (id: string) => {
    try {
      await api.delete(`/admin/ai/test-runs/${id}`);
      toast.success('Run verwijderd');
      const gevonden = await laadTestset();
      if (gekozenRun === id) setGekozenRun(gevonden[0]?.id ?? null);
      if (vergelijkRun === id) setVergelijkRun(null);
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Verwijderen mislukt');
    }
  };

  // --- Vergelijking -------------------------------------------------------

  const vorigePerTranscriptie = useMemo(() => {
    const kaart = new Map<string, AiTestUitkomst>();
    for (const uitkomst of vergelijkDetail?.results ?? []) {
      kaart.set(uitkomst.transcriptId, uitkomst);
    }
    return kaart;
  }, [vergelijkDetail]);

  const gekozenSamenvatting = runs.find((r) => r.id === gekozenRun) ?? null;
  const vergelijkSamenvatting = runs.find((r) => r.id === vergelijkRun) ?? null;

  const promptGewijzigd =
    gekozenSamenvatting &&
    vergelijkSamenvatting &&
    gekozenSamenvatting.promptFingerprint !== vergelijkSamenvatting.promptFingerprint;
  const modelGewijzigd =
    gekozenSamenvatting &&
    vergelijkSamenvatting &&
    gekozenSamenvatting.model !== vergelijkSamenvatting.model;

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-3">
      <p className="text-xs text-muted-foreground">
        Leg transcripties vast met de verwachte uitkomst in gedachten en laat ze als set draaien.
        Elke uitkomst wordt met datum, model en een vingerafdruk van de prompts bewaard, zodat na
        het wijzigen van een model of een prompt te zien is wat er anders uit komt.
      </p>

      {/* ---- De testset ---------------------------------------------------- */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">
            Testset{' '}
            <span className="text-muted-foreground font-normal">
              ({actieveTranscripties.length} actief van {transcripties.length})
            </span>
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            disabled={concept !== null || voortgang !== null}
            onClick={() => setConcept({ ...LEEG_CONCEPT })}
          >
            <Plus className="h-3 w-3 mr-1" /> Transcriptie toevoegen
          </Button>
        </div>

        {transcripties.length === 0 && !concept && (
          <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            Nog geen testtranscripties. Voeg er een toe om de eerste run te kunnen draaien.
          </p>
        )}

        <div className="space-y-1.5">
          {transcripties.map((transcriptie) => (
            <div
              key={transcriptie.id}
              className={`rounded-md border p-3 space-y-1 ${transcriptie.isActive ? 'bg-background' : 'bg-muted/40'}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium truncate">{transcriptie.name}</span>
                    <Badge variant="outline" className="text-[10px]">
                      {transcriptie.multipleProblems ? 'meervoudig' : 'enkelvoudig'}
                    </Badge>
                    {transcriptie.resultCount > 0 && (
                      <Badge variant="secondary" className="text-[10px]">
                        {transcriptie.resultCount} uitkomst
                        {transcriptie.resultCount === 1 ? '' : 'en'}
                      </Badge>
                    )}
                    {!transcriptie.isActive && (
                      <Badge variant="secondary" className="text-[10px]">
                        inactief
                      </Badge>
                    )}
                  </div>
                  {transcriptie.notes && (
                    <p className="text-xs text-muted-foreground">{transcriptie.notes}</p>
                  )}
                  <p className="text-xs text-muted-foreground line-clamp-2 font-mono">
                    {transcriptie.transcript}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Switch
                    checked={transcriptie.isActive}
                    disabled={voortgang !== null}
                    onCheckedChange={(aan) => void zetActief(transcriptie, aan)}
                    aria-label="Meenemen in een run"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={voortgang !== null}
                    title="Bewerken"
                    onClick={() =>
                      setConcept({
                        id: transcriptie.id,
                        name: transcriptie.name,
                        transcript: transcriptie.transcript,
                        multipleProblems: transcriptie.multipleProblems,
                        notes: transcriptie.notes ?? '',
                      })
                    }
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                    disabled={voortgang !== null}
                    title={
                      transcriptie.resultCount > 0
                        ? 'Deze transcriptie heeft bewaarde uitkomsten; zet hem op inactief'
                        : 'Verwijderen'
                    }
                    onClick={() => setTeVerwijderen(transcriptie)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {concept && (
          <div className="rounded-md border border-primary/40 bg-background p-3 space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Naam</Label>
                <Input
                  value={concept.name}
                  onChange={(e) => setConcept({ ...concept, name: e.target.value })}
                  placeholder="Bijv. Hoofdpijn met verhoogde bloeddruk"
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Waar let je op (optioneel)</Label>
                <Input
                  value={concept.notes}
                  onChange={(e) => setConcept({ ...concept, notes: e.target.value })}
                  placeholder="Bijv. bloeddruk hoort in O, geen diagnose verzinnen"
                  className="h-8 text-sm"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Transcriptie</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs"
                  onClick={() => setConcept({ ...concept, transcript: VOORBEELD_TRANSCRIPT })}
                >
                  📝 Voorbeeld invullen
                </Button>
              </div>
              <Textarea
                value={concept.transcript}
                onChange={(e) => setConcept({ ...concept, transcript: e.target.value })}
                rows={8}
                className="text-xs font-mono"
                placeholder="Plak hier het consultgesprek..."
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={concept.multipleProblems}
                onCheckedChange={(aan) => setConcept({ ...concept, multipleProblems: aan })}
                id="testset-meervoudig"
              />
              <Label htmlFor="testset-meervoudig" className="text-xs font-normal">
                Meerdere problemen toegestaan (gebruikt de meervoudige SOEP-prompt)
              </Label>
            </div>
            <div className="flex gap-2">
              <Button type="button" size="sm" disabled={isBewaren} onClick={() => void bewaarConcept()}>
                {isBewaren ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    Opslaan...
                  </>
                ) : (
                  <>
                    <Check className="h-3.5 w-3.5 mr-1" />
                    {concept.id ? 'Wijziging opslaan' : 'Toevoegen'}
                  </>
                )}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={isBewaren}
                onClick={() => setConcept(null)}
              >
                <X className="h-3.5 w-3.5 mr-1" /> Annuleren
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ---- Draaien ------------------------------------------------------- */}
      <div className="space-y-2 border-t pt-4">
        <Label className="text-sm font-medium">Testset draaien</Label>
        <div className="flex flex-wrap gap-2 items-center">
          <Input
            value={runLabelInvoer}
            onChange={(e) => setRunLabelInvoer(e.target.value)}
            placeholder="Aanleiding, bijv. 'na overstap naar gpt-4.1' (optioneel)"
            className="h-8 text-sm max-w-sm"
            disabled={voortgang !== null}
          />
          <Button
            type="button"
            size="sm"
            disabled={voortgang !== null || actieveTranscripties.length === 0}
            onClick={() => void draaiTestset()}
          >
            {voortgang ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                {voortgang.klaar}/{voortgang.totaal}
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-1" />
                {actieveTranscripties.length} transcriptie
                {actieveTranscripties.length === 1 ? '' : 's'} draaien
              </>
            )}
          </Button>
        </div>
        {voortgang && (
          <div className="space-y-1">
            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{
                  width: `${voortgang.totaal === 0 ? 0 : (voortgang.klaar / voortgang.totaal) * 100}%`,
                }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {voortgang.bezigMet ? `Bezig met: ${voortgang.bezigMet}` : 'Afronden...'} — laat dit
              scherm open tot de run klaar is; elke uitkomst wordt direct bewaard.
            </p>
          </div>
        )}
        {actieveTranscripties.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Er staat geen actieve transcriptie in de testset.
          </p>
        )}
      </div>

      {/* ---- Runs en vergelijking ------------------------------------------ */}
      {runs.length > 0 && (
        <div className="space-y-3 border-t pt-4">
          <Label className="text-sm font-medium">Runs vergelijken</Label>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Deze run</Label>
              <Select value={gekozenRun ?? ''} onValueChange={(v) => setGekozenRun(v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Kies een run" />
                </SelectTrigger>
                <SelectContent>
                  {runs.map((run) => (
                    <SelectItem key={run.id} value={run.id} className="text-xs">
                      {runLabel(run)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Vergelijken met</Label>
              <Select
                value={vergelijkRun ?? 'geen'}
                onValueChange={(v) => setVergelijkRun(v === 'geen' ? null : v)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="geen" className="text-xs">
                    Niet vergelijken
                  </SelectItem>
                  {runs
                    .filter((run) => run.id !== gekozenRun)
                    .map((run) => (
                      <SelectItem key={run.id} value={run.id} className="text-xs">
                        {runLabel(run)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {gekozenSamenvatting && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>
                {gekozenSamenvatting.count} uitkomst
                {gekozenSamenvatting.count === 1 ? '' : 'en'}
              </span>
              {gekozenSamenvatting.failed > 0 && (
                <Badge variant="destructive" className="text-[10px]">
                  {gekozenSamenvatting.failed} mislukt
                </Badge>
              )}
              <span>
                Tokens: {gekozenSamenvatting.usage.promptTokens} in /{' '}
                {gekozenSamenvatting.usage.completionTokens} out
              </span>
              {gekozenSamenvatting.promptFingerprint && (
                <span className="font-mono">prompt {gekozenSamenvatting.promptFingerprint}</span>
              )}
              {!gekozenSamenvatting.finishedAt && (
                <Badge variant="outline" className="text-[10px]">
                  niet afgerond
                </Badge>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 text-xs text-destructive hover:text-destructive"
                disabled={voortgang !== null}
                onClick={() => void verwijderRun(gekozenSamenvatting.id)}
              >
                <Trash2 className="h-3 w-3 mr-1" /> Run verwijderen
              </Button>
            </div>
          )}

          {vergelijkSamenvatting && (modelGewijzigd || promptGewijzigd) && (
            <div className="rounded-md border bg-muted/30 p-2 text-xs space-y-0.5">
              {modelGewijzigd && (
                <p>
                  Model: <span className="font-mono">{vergelijkSamenvatting.model ?? '—'}</span> →{' '}
                  <span className="font-mono">{gekozenSamenvatting?.model ?? '—'}</span>
                </p>
              )}
              {promptGewijzigd && (
                <p>
                  Prompt gewijzigd:{' '}
                  <span className="font-mono">{vergelijkSamenvatting.promptFingerprint ?? '—'}</span>{' '}
                  → <span className="font-mono">{gekozenSamenvatting?.promptFingerprint ?? '—'}</span>
                </p>
              )}
            </div>
          )}
          {vergelijkSamenvatting && !modelGewijzigd && !promptGewijzigd && (
            <p className="text-xs text-muted-foreground">
              Zelfde model en zelfde prompt als de vergelijkingsrun — een verschil hieronder komt
              dan van het model zelf, dat bij dezelfde invoer niet altijd hetzelfde antwoord geeft.
            </p>
          )}

          {isDetailLaden && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Uitkomsten laden...
            </div>
          )}

          <div className="space-y-2">
            {detail?.results.map((uitkomst) => {
              const vorige = vorigePerTranscriptie.get(uitkomst.transcriptId) ?? null;
              /**
               * Bij een mislukte aanroep zijn de vier velden leeg, en dan zou elk
               * gevuld veld van de vorige run als "gewijzigd" gelden. Dat leest als
               * een model dat iets anders vond in plaats van een aanroep die faalde,
               * dus bij een fout wordt er niet vergeleken.
               */
              const gewijzigdeVelden = vorige && !uitkomst.error
                ? SOEP_VELDEN.filter(
                    (veld) =>
                      !gelijkNaNormalisatie(
                        vorige.soep[veld.sleutel as SoepSleutel],
                        uitkomst.soep[veld.sleutel as SoepSleutel],
                      ),
                  ).map((veld) => veld.letter)
                : [];

              return (
                <div key={uitkomst.id} className="rounded-md border p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">
                        {uitkomst.transcriptName ?? 'Onbekende transcriptie'}
                      </span>
                      {uitkomst.multipleProblems && (
                        <Badge variant="outline" className="text-[10px]">
                          meervoudig
                        </Badge>
                      )}
                      {uitkomst.error && (
                        <Badge variant="destructive" className="text-[10px]">
                          mislukt
                        </Badge>
                      )}
                      {vorige && !uitkomst.error && gewijzigdeVelden.length === 0 && (
                        <Badge variant="secondary" className="text-[10px]">
                          ongewijzigd
                        </Badge>
                      )}
                      {vorige && gewijzigdeVelden.length > 0 && (
                        <Badge variant="default" className="text-[10px]">
                          gewijzigd: {gewijzigdeVelden.join(', ')}
                        </Badge>
                      )}
                      {!vorige && vergelijkRun && !uitkomst.error && (
                        <Badge variant="outline" className="text-[10px]">
                          geen vorige uitkomst
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {uitkomst.model ?? '—'}
                      {uitkomst.durationMs !== null && ` · ${(uitkomst.durationMs / 1000).toFixed(1)}s`}
                      {' · '}
                      {uitkomst.usage.promptTokens} in / {uitkomst.usage.completionTokens} out
                    </span>
                  </div>

                  {uitkomst.error ? (
                    <div className="rounded-md border border-destructive/50 bg-destructive/10 p-2 text-sm text-destructive flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>{uitkomst.error}</span>
                    </div>
                  ) : (
                    <div className="space-y-1 text-sm">
                      {SOEP_VELDEN.map((veld) => {
                        const nu = uitkomst.soep[veld.sleutel as SoepSleutel];
                        const toen = vorige?.soep[veld.sleutel as SoepSleutel] ?? null;
                        const isGewijzigd = toen !== null && !gelijkNaNormalisatie(toen, nu);

                        return (
                          <div key={veld.sleutel} className="flex gap-2">
                            <span
                              className="font-semibold w-4 shrink-0"
                              title={veld.naam}
                            >
                              {veld.letter}:
                            </span>
                            <div className="min-w-0">
                              {isGewijzigd ? (
                                <DiffTekst oud={toen!} nieuw={nu} />
                              ) : nu ? (
                                <span className="whitespace-pre-wrap">{nu}</span>
                              ) : (
                                <span className="text-muted-foreground italic">leeg</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <IcpcRegel nu={uitkomst.icpc} toen={vorige?.icpc ?? null} />
                </div>
              );
            })}
          </div>

          {detail && detail.results.length === 0 && !isDetailLaden && (
            <p className="text-sm text-muted-foreground">Deze run heeft geen uitkomsten.</p>
          )}
        </div>
      )}

      <AlertDialog open={teVerwijderen !== null} onOpenChange={(open) => !open && setTeVerwijderen(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Testtranscriptie verwijderen?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <span className="block">
                  “{teVerwijderen?.name}” wordt uit de testset verwijderd.
                </span>
                {(teVerwijderen?.resultCount ?? 0) > 0 && (
                  <span className="block text-destructive">
                    Op deze transcriptie staan {teVerwijderen?.resultCount} bewaarde uitkomsten.
                    Verwijderen lukt daarom niet — zet hem op inactief om hem uit nieuwe runs te
                    houden zonder de vergelijkingen kwijt te raken.
                  </span>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => void verwijderTranscriptie()}
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
